<template>
	<b-tabs
		v-model="topLevelTabIndex"
		pills
		card
		vertical
		class="file-browser"
	>
		<b-tab title="Choose Existing File" active>
			<div id="file-browser-existing">
				<b-tabs v-model="existingFileTabIndex" lazy>
					<b-tab title="My Files" @click="getFiles('user')">
						<div id="file-browser-existing-user">
							<div v-if="fetching">
								<b-spinner label="Loading" />
							</div>
							<div v-else>
								<b-input-group>
									<b-form-input v-model="searchQuery" />
									<b-input-group-append>
										<b-button variant="primary" @click="searchFiles">
											<b-icon-search />
										</b-button>
										<b-button variant="x" @click="clearSearch">
											<b-icon-x />
										</b-button>
									</b-input-group-append>
								</b-input-group>
								<div v-if="fileList.length > 0" class="file-browser-file-list">
									<b-list-group v-for="file in fileList" :key="file.id">
										<b-list-group-item
											:data-id="file.id"
											:data-name="file.name"
											:active="file.id === selectedFile"
											@click="selectFile"
										>
											{{ file.name }}
											<b-collapse :visible="file.id === selectedFile" class="small">
												Last Updated: {{ formatDateTime(file.updatedAt) }}
											</b-collapse>
										</b-list-group-item>
									</b-list-group>
								</div>
								<div v-else class="file-browser-no-files">
									No Files
								</div>
							</div>
						</div>
					</b-tab>
					<b-tab title="Enterprise Files" @click="getFiles('enterprise')">
						<div id="file-browser-existing-enterprise">
							<div v-if="fetching">
								<b-spinner label="Loading" />
							</div>
							<div v-else>
								<b-input-group>
									<b-form-input v-model="searchQuery" />
									<b-input-group-append>
										<b-button variant="primary" @click="searchFiles">
											<b-icon-search />
										</b-button>
										<b-button variant="x" @click="clearSearch">
											<b-icon-x />
										</b-button>
									</b-input-group-append>
								</b-input-group>
								<div v-if="fileList.length > 0" class="file-browser-file-list">
									<b-list-group v-for="file in fileList" :key="file.id">
										<b-list-group-item
											:data-id="file.id"
											:data-name="file.name"
											:active="file.id === selectedFile"
											@click="selectFile"
										>
											{{ file.name }}
											<b-collapse :visible="file.id === selectedFile" class="small">
												<span v-if="file.user">Uploaded by: {{ file.user.lastName }}, {{ file.user.firstName }}<br></span>
												Last Updated: {{ formatDateTime(file.updatedAt) }}
											</b-collapse>
										</b-list-group-item>
									</b-list-group>
								</div>
								<div v-else>
									No Files
								</div>
							</div>
						</div>
					</b-tab>
				</b-tabs> 
			</div>
		</b-tab>
		<b-tab title="Upload a New File">
			<div id="file-browser-upload" class="file-upload">
				<div class="mt-5">
					{{ $t('file-manager.drag-and-drop') }}
				</div>
				<div class="mt-3">
					{{ $t('file-manager.or') }}
				</div>
				<button class="browse-files" @click="clickInput">
					{{ $t('file-manager.browse-files') }}
				</button>
				<div class="selected-file">
					{{ uploadFileName }}
				</div>
				<input
					ref="fileInput"
					type="file"
					hidden
					@change="prepareFileUpload($event)"
				>
			</div>
		</b-tab>
	</b-tabs>
</template>

<script>
import { DateTime } from 'luxon';

export default {
	name: 'FileBrowser',
	props: {
		initialTab: { type: String, default: '' },
	},
	data() {
		return {
			uploadFileName: this.$t('file-manager.no-file-selected'),
			fileList: [],
			topLevelTab: this.initialTab || 'file-browser-existing',
			topLevelTabs: ['file-browser-existing', 'file-browser-upload'],
			activeExistingFileTab: 'user',
			existingFileTabs: ['file-browser-existing-user', 'file-browser-existing-enterprise'],
			topLevelTabIndex: 0,
			existingFileTabIndex: 0,
			selectedFile: null,
			fetching: false,
			searchQuery: null,
			// uploaded files
			fileHash: null,
			fileToUpload: null,
		};
	},
	created() {
		this.fetchFileListing(this.activeExistingFileTab);
	},
	methods: {
		formatDateTime(value) {
			return DateTime.fromISO(value).toLocaleString(DateTime.DATETIME_SHORT);
		},
		clickInput() {
			this.$refs.fileInput.click();
		},
		clearFileList() {
			this.fileList = [];
		},
		selectFile(event) {
			try {
				this.selectedFile = event.target.dataset.id;
				this.$emit('fileSelected', `dmp:file:${this.selectedFile}`);
			} catch (error) {
				console.error(error);
			}
		},
		getFiles(endpoint) {
			if (this.activeExistingFileTab !== endpoint) {
				this.searchQuery = null;
				this.clearFileList();
				this.activeExistingFileTab = endpoint;
				this.fetchFileListing(endpoint);
			}
		},
		buildFileUrl(endpoint, params) {
			let url = '/files';
			if (endpoint === 'enterprise') {
				url = `${url}/${endpoint}`;
			}
			const qp = new URLSearchParams();
			for (const key of Object.keys(params)) {
				qp.set(key, params[key]);
			}
			url = `${url}?${qp.toString()}`;

			return url;
		},
		async fetchFileListing(endpoint, limit = 50, offset = 0) {
			// fetchFileListing should not be called directly; will append to fileList
			this.fetching = true;
			const url = this.buildFileUrl(endpoint, { limit, offset });
			this.axios
				.get(url)
				.then((response) => {
					this.fetching = false;
					this.fileList = this.fileList.concat(response.data);
				})
				.catch((error) => {
					console.error(error);
				});
		},
		async searchFiles() {
			this.fetching = true;
			this.fileList = [];
			const url = this.buildFileUrl(this.activeExistingFileTab, { name: this.searchQuery });
			this.axios
				.get(url)
				.then((response) => {
					this.fetching = false;
					this.fileList = this.fileList.concat(response.data);
					if (this.fileList.length === 1) this.selectedFile = this.fileList[0].id;
				})
				.catch((error) => {
					console.error(error);
				});
		},
		clearSearch() {
			this.clearFileList();
			this.$emit('fileSelected', null);
			this.fetchFileListing(this.activeExistingFileTab);
		},
		prepareFileUpload(event) {
			// Read the file and compute its SHA-256 hash value
			// Pass the hash value to the /files/hash/{id} endpoint via checkFileHash
			[this.fileToUpload] = event.target.files;
			const fileReader = new FileReader();
			const that = this;
			fileReader.onload = function fileReaderOnload(e) {
				crypto.subtle
					.digest('SHA-256', e.target.result)
					.then((hashBuffer) => {
						const hashArray = Array.from(new Uint8Array(hashBuffer));
						that.fileHash = hashArray.map((bytes) => bytes.toString(16).padStart(2, '0')).join('');
						that.checkFileHash(that.fileHash);
					})
					.catch((error) => {
						console.error(error);
						this.fileHash = null;
					});
			};
			fileReader.readAsArrayBuffer(this.fileToUpload);
		},
		checkFileHash(fileHash) {
			// Call /files/hash to determine if file is previously uploaded
			// If 204 (No Content) there is no matching file, so upload
			// If 200, the metadata of the matching file is returned, so continue as if you uploaded
			this.axios.get(`/files/hash/${fileHash}`).then((response) => {
				if (response.status === 204) {
					// no content, so upload file
					this.uploadSelectedFile();
				} else {
					this.selectedFile = response.data.id;
					this.$emit('fileSelected', `dmp:file:${this.selectedFile}`);
					this.topLevelTabIndex = this.topLevelTabs.indexOf('file-browser-existing');
					if (response.data.userId === this.$store.state.user.id) {
						// file.user.id = user.id then show file in user list, otherwise show in enterprise
						this.existingFileTabIndex = this.existingFileTabs.indexOf('file-browser-existing-user');
					} else {
						this.existingFileTabIndex = this.existingFileTabs.indexOf('file-browser-existing-enterprise');
					}
				}
			});
		},
		uploadSelectedFile() {
			// upload file
			const formData = new FormData();
			formData.append('file', this.fileToUpload);

			this.axios
				.put(`/files`, formData, {
					headers: {
						'Content-Type': 'multipart/form-data',
					},
				})
				.then((response) => {
					this.fileList.push(response.data);
					this.selectedFile = response.data.id;
					this.$emit('fileSelected', `dmp:file:${this.selectedFile}`);
					this.topLevelTabIndex = this.topLevelTabs.indexOf('file-browser-existing');
					this.existingFileTabIndex = this.existingFileTabs.indexOf('file-browser-existing-user');
				})
				.catch((error) => {
					console.error(error);
				});
		},
	},
};
</script>

<style>
.file-browser {
	min-height: 10%;
	max-height: 30%;
}
.file-browser-file-list {
	height: 100%;
	overflow-y: scroll;
}
.file-browser-no-files {
	text-align: center;
	font-weight: bold;
	font-size: 1.4rem;
}
.file-upload {
	border: dashed #333333;
	border-radius: 15px;
	color: #979797;
	text-align: center;
}
</style>
